import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { Data, ParamMap } from '@angular/router';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, firstValueFrom } from 'rxjs';
import { MessageService, ConfirmationService, FilterService } from 'primeng/api';
import { LoggerService } from 'src/app/common/services/logger.service';
import { Table } from 'primeng/table';

@Component({
  selector: 'app-localization-table',
  templateUrl: './localization-table.component.html',
  styleUrls: ['./localization-table.component.sass'],
  providers: [MessageService, ConfirmationService]
})
export class LocalizationTableComponent implements OnInit {
  @ViewChild('locTable') locTable!: Table;

  @Input() tableName: string = 'colors'; // Nombre de la tabla, por defecto 'colors'
  @Input() tableTitle: string = 'Colors'; // Título para mostrar
  
  records: any[] = [];
  loading: boolean = false;
  clonedRecords: { [s: string]: any } = {};
  translatingRecords: { [key: string]: boolean } = {}; // Track which records are being translated

  // Row selection for bulk localization (one or multiple records)
  selectedRecords: any[] = [];
  
  // Pagination properties
  totalRecords: number = 0;
  currentPage: number = 1;
  rowsPerPage: number = 50;
  sortBy: string = 'recordId';
  sortOrder: string = 'asc';

  // Filter properties - PrimeNG filter format
  filters: { [key: string]: any } = {};

  // Actions dialog visibility
  actionsDialogVisible: boolean = false;

  // Bulk localize properties
  bulkLocalizeIdRanges: string = '';
  bulkLocalizeEntity: string = '';
  bulkLocalizeInProgress: boolean = false;
  bulkLocalizeProgress: number = 0;
  bulkLocalizeResult: { success: boolean; message: string } | null = null;

  // Tables that support Migrate from Gridly (webadmin)  
  isMigrateFromGridlyTable: boolean = false;
  // Tables with webadmin-style schema (recordId PK, no field column) e.g. webadmin. Webadmin also has maxCharacterLength.
  isSpecialTable: boolean = false;
  migrateViewId: string = '';
  migrateBatchSize: number = 100;
  migrateInProgress: boolean = false;
  migrateResult: { success: boolean; totalProcessed?: number; batchesProcessed?: number; errors?: string[] } | null = null;

  // Table bulk translate (recordIds are strings; used for webadmin table)
  tableTranslateRecordIds: string = '';
  tableTranslateInProgress: boolean = false;
  tableTranslateTargetFr: boolean = true;
  tableTranslateTargetEs: boolean = true;
  tableTranslateTargetDe: boolean = true;
  tableTranslateResult: { success: boolean; queued?: number; error?: string } | null = null;

  // Custom match mode options for PrimeNG column filters
  // These will be available in the filter dropdown menu
  matchModeOptions = [
    { label: 'Contains', value: 'contains' },
    { label: 'Equals', value: 'equals' },
    { label: 'Starts With', value: 'startsWith' },
    { label: 'Ends With', value: 'endsWith' },
    { label: 'Match (Regex)', value: 'regex' },
    { label: 'Find Empty', value: 'empty' },
    { label: 'Find Non Empty', value: 'nonEmpty' },
    { label: 'Includes String', value: 'includes' },
    { label: 'Does Not Include', value: 'notIncludes' }
  ];

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute,
    private filterService: FilterService,
    private logger: LoggerService
  ) {
    // Register custom filter match modes
    this.registerCustomFilters();
  }

  private registerCustomFilters() {
    // Register regex filter
    this.filterService.register('regex', (value: any, filter: any): boolean => {
      if (filter === undefined || filter === null || filter === '') {
        return true;
      }
      if (value === undefined || value === null) {
        return false;
      }
      try {
        const regex = new RegExp(filter, 'i');
        return regex.test(String(value));
      } catch (e) {
        return false;
      }
    });

    // Register empty filter
    this.filterService.register('empty', (value: any): boolean => {
      return value === null || value === undefined || value === '' || String(value).trim() === '';
    });

    // Register non-empty filter
    this.filterService.register('nonEmpty', (value: any): boolean => {
      return value !== null && value !== undefined && value !== '' && String(value).trim() !== '';
    });

    // Register includes filter (alias for contains)
    this.filterService.register('includes', (value: any, filter: any): boolean => {
      if (filter === undefined || filter === null || filter === '') {
        return true;
      }
      if (value === undefined || value === null) {
        return false;
      }
      return String(value).toLowerCase().includes(String(filter).toLowerCase());
    });

    // Register not-includes filter
    this.filterService.register('notIncludes', (value: any, filter: any): boolean => {
      if (filter === undefined || filter === null || filter === '') {
        return true;
      }
      if (value === undefined || value === null) {
        return true; // null/undefined values are considered as "not including"
      }
      return !String(value).toLowerCase().includes(String(filter).toLowerCase());
    });
  }

  ngOnInit() {
    this.applyRouteContext(this.route.snapshot.paramMap, this.route.snapshot.data);
    // Defer loading to next tick to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.loadRecords();
    }, 0);
    combineLatest([this.route.paramMap, this.route.data]).subscribe(([params, data]) => {
      const beforeTable = this.tableName;
      this.applyRouteContext(params, data);
      if (this.tableName && this.tableName !== beforeTable) {
        this.loadRecords(1);
      }
    });
  }

  private applyRouteContext(params: ParamMap, data: Data) {
    const tableName = data['tableName'] || params.get('tableName');
    if (tableName) {
      this.tableName = tableName;
      this.bulkLocalizeEntity = tableName;
    }
    this.tableTitle = data['tableTitle'] || this.formatTableTitle(this.tableName);
    if (!this.bulkLocalizeEntity && this.tableName) {
      this.bulkLocalizeEntity = this.tableName;
    }
    this.isMigrateFromGridlyTable = this.tableName === 'webadmin' || this.tableName === 'pushNotifications';
    this.isSpecialTable = this.tableName === 'webadmin' || this.tableName === 'pushNotifications';
  }

  private formatTableTitle(name: string): string {
    if (!name) {
      return '';
    }
    return name
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getRowKey(record: any): string {
    return this.isSpecialTable ? String(record.recordId) : `${record.recordId}_${record.field}`;
  }

  async loadRecords(page: number = 1, filters?: any) {
    this.loading = true;
    try {
      // Backend expects 0-based page, frontend uses 1-based
      const backendPage = page - 1;
      
      // Build query parameters (page and pageSize)
      let params = new HttpParams()
        .set('page', backendPage.toString())
        .set('pageSize', this.rowsPerPage.toString());

      // Use provided filters or current filters
      const activeFilters = filters || this.filters;

      // Debug: Log filters being processed
      this.logger.log('loadRecords - activeFilters:', JSON.stringify(activeFilters, null, 2));
      this.logger.log('loadRecords - filters parameter:', JSON.stringify(filters, null, 2));
      this.logger.log('loadRecords - this.filters:', JSON.stringify(this.filters, null, 2));

      // Build WHERE clause from PrimeNG filters
      const whereConditions: string[] = [];
      const whereParams: any[] = [];
      
      // Handle case where activeFilters might be null/undefined
      if (!activeFilters || Object.keys(activeFilters).length === 0) {
        this.logger.log('No active filters to process');
      }
      
      Object.keys(activeFilters || {}).forEach(key => {
        const filter = activeFilters[key];
        this.logger.log(`Processing filter for key: ${key}`, filter);
        
        if (!filter) {
          this.logger.log(`Skipping ${key} - filter is null/undefined`);
          return;
        }
        
        // PrimeNG filters can be:
        // 1. An array of constraint objects: [{value: "02", matchMode: "endsWith", operator: "and"}]
        // 2. An object with constraints array: {constraints: [{...}]}
        // 3. A direct filter object: {value: "...", matchMode: "..."}
        let filterObj = null;
        
        if (Array.isArray(filter)) {
          // Filter is an array of constraints
          if (filter.length > 0) {
            filterObj = filter[0];
          }
        } else if (filter.constraints && Array.isArray(filter.constraints) && filter.constraints.length > 0) {
          // Filter has a constraints array
          filterObj = filter.constraints[0];
        } else if (filter.matchMode || filter.value !== undefined) {
          // Filter is a direct object
          filterObj = filter;
        }
        
        if (!filterObj) {
          this.logger.log(`Skipping ${key} - could not extract filter object`);
          return;
        }
        
        const matchMode = filterObj.matchMode || 'contains';
        this.logger.log(`Filter ${key} - matchMode: ${matchMode}, value:`, filterObj.value);
        
        // Handle empty and nonEmpty filters (don't require a value)
        if (matchMode === 'empty') {
          whereConditions.push(`(${key} IS NULL OR ${key} = '' OR ${key} = 'null')`);
          this.logger.log(`Added empty condition for ${key}`);
          // No params needed for empty check
        } else if (matchMode === 'nonEmpty') {
          whereConditions.push(`(${key} IS NOT NULL AND ${key} != '' AND ${key} != 'null')`);
          this.logger.log(`Added nonEmpty condition for ${key}`);
          // No params needed for non-empty check
        } else {
          // All other filters require a value
          if (filterObj.value === null || filterObj.value === undefined || filterObj.value === '') {
            this.logger.log(`Skipping ${key} - no value provided`);
            return; // Skip filters without values
          }
          
          const filterValue = String(filterObj.value).trim();
          if (!filterValue) {
            this.logger.log(`Skipping ${key} - empty value after trim`);
            return;
          }
          
          this.logger.log(`Processing ${key} with matchMode: ${matchMode}, value: ${filterValue}`);
          
          if (key === 'recordId') {
            // For recordId, handle different match modes
            if (matchMode === 'equals') {
              whereConditions.push('recordId = ?');
              whereParams.push(parseInt(filterValue) || filterValue);
            } else if (matchMode === 'regex') {
              whereConditions.push('recordId REGEXP ?');
              whereParams.push(filterValue);
            } else if (matchMode === 'notIncludes') {
              whereConditions.push('recordId NOT LIKE ?');
              whereParams.push(`%${filterValue}%`);
            } else {
              // Default to contains/includes
              whereConditions.push('recordId LIKE ?');
              whereParams.push(`%${filterValue}%`);
            }
          } else if (key === 'updatedAt') {
            // For date filtering
            if (matchMode === 'regex') {
              whereConditions.push('updatedAt REGEXP ?');
              whereParams.push(filterValue);
            } else if (matchMode === 'notIncludes') {
              whereConditions.push('updatedAt NOT LIKE ?');
              whereParams.push(`%${filterValue}%`);
            } else {
              // Default to contains
              whereConditions.push('updatedAt LIKE ?');
              whereParams.push(`%${filterValue}%`);
            }
          } else {
            // For text fields, handle all match modes
            if (matchMode === 'equals') {
              whereConditions.push(`${key} = ?`);
              whereParams.push(filterValue);
            } else if (matchMode === 'startsWith') {
              whereConditions.push(`${key} LIKE ?`);
              whereParams.push(`${filterValue}%`);
            } else if (matchMode === 'endsWith') {
              whereConditions.push(`${key} LIKE ?`);
              whereParams.push(`%${filterValue}`);
            } else if (matchMode === 'regex') {
              whereConditions.push(`${key} REGEXP ?`);
              whereParams.push(filterValue);
            } else if (matchMode === 'notIncludes') {
              whereConditions.push(`${key} NOT LIKE ?`);
              whereParams.push(`%${filterValue}%`);
            } else {
              // Default to contains/includes
              whereConditions.push(`${key} LIKE ?`);
              whereParams.push(`%${filterValue}%`);
            }
          }
        }
      });

      const whereClause = whereConditions.length > 0 
        ? whereConditions.join(' AND ')
        : undefined;

      this.logger.log('Final WHERE clause:', whereClause);
      this.logger.log('Final WHERE params:', whereParams);

      // Build request body (sort, sortOrder, whereClause, and params)
      const body: any = {
        sort: this.sortBy,
        sortOrder: this.sortOrder.toUpperCase() as 'ASC' | 'DESC'
      };

      if (whereClause) {
        body.whereClause = whereClause;
        body.params = whereParams;
      }
      
      this.logger.log('Request body:', body);

      const response = await firstValueFrom(
        this.http.post<any>(`/api/localization/db/table/${this.tableName}/paginated`, body, { params })
      );

      // Update records and pagination info
      // Backend returns { records, paginationMeta: { totalRecords } }
      const rawRecords = response.records || [];
      this.records = rawRecords.map((r: any) => ({ ...r, rowKey: this.getRowKey(r) }));
      this.totalRecords = response.paginationMeta?.totalRecords || 0;
      this.currentPage = page;
      this.selectedRecords = []; // Clear selection when data changes
      
      this.logger.log(`${this.tableName} loaded:`, this.records);
    } catch (error) {
      this.logger.error(`Error loading ${this.tableName}:`, error);
      
      // Check if error is due to endpoint not existing (HTML response)
      let errorMessage = `Failed to load ${this.tableName}`;
      if (error instanceof HttpErrorResponse) {
        const errorText = error.error?.text || '';
        if (errorText.includes('<!DOCTYPE html>') || errorText.includes('<html')) {
          errorMessage = `Backend endpoint not available yet. The paginated endpoint for ${this.tableName} is not implemented.`;
        } else if (error.status === 404) {
          errorMessage = `Endpoint not found: /api/localization/db/table/${this.tableName}/paginated`;
        } else if (error.status) {
          errorMessage = `Failed to load ${this.tableName} (Status: ${error.status})`;
        }
      }
      
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage
      });
    } finally {
      this.loading = false;
    }
  }

  onPageChange(event: any) {
    // Calculate page number from event.first and event.rows
    const page = Math.floor(event.first / event.rows) + 1;
    
    // Update rowsPerPage if changed
    if (event.rows !== this.rowsPerPage) {
      this.rowsPerPage = event.rows;
    }
    
    // Update sorting if provided in the event
    // PrimeNG sends sortOrder as 1 (ascending) or -1 (descending)
    if (event.sortField !== undefined && event.sortField !== null) {
      this.sortBy = event.sortField;
      this.sortOrder = event.sortOrder === 1 ? 'asc' : 'desc';
      this.logger.log(`Sorting changed: ${this.sortBy} ${this.sortOrder}`);
    }
    
    // Debug: Log the filter event structure
    this.logger.log('onPageChange - event.filters:', event.filters);
    this.logger.log('onPageChange - event.sortField:', event.sortField);
    this.logger.log('onPageChange - event.sortOrder:', event.sortOrder);
    this.logger.log('onPageChange - full event:', event);
    
    // Update filters from PrimeNG filter event
    if (event.filters) {
      this.filters = event.filters;
    }
    
    // Call loadRecords with the new page and filters
    this.loadRecords(page, event.filters);
  }

  onRowEditInit(record: any) {
    this.clonedRecords[this.getRowKey(record)] = { ...record };
  }

  private getRowElementFromSaveClick(event: Event): HTMLTableRowElement | null {
    const target = event?.target as HTMLElement | null;
    return target?.closest?.('tr') ?? null;
  }

  async onRowEditSave(record: any, event: Event) {
    const rowElement = this.getRowElementFromSaveClick(event);

    const sourceEnTrimmed =
      record.Source_en == null ? '' : String(record.Source_en).trim();
    if (sourceEnTrimmed === '') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'English (Source) cannot be empty.'
      });
      return;
    }

    const rowKey = this.getRowKey(record);
    const originalRecord = this.clonedRecords[rowKey];

    const changes: string[] = [];
    if (originalRecord.Source_en !== record.Source_en) {
      changes.push(`English: "${originalRecord.Source_en}" → "${record.Source_en}"`);
    }
    if (originalRecord.Target_frFR !== record.Target_frFR) {
      changes.push(`French: "${originalRecord.Target_frFR}" → "${record.Target_frFR}"`);
    }
    if (originalRecord.Target_esMx !== record.Target_esMx) {
      changes.push(`Spanish: "${originalRecord.Target_esMx}" → "${record.Target_esMx}"`);
    }
    if (originalRecord.Target_deDE !== record.Target_deDE) {
      changes.push(`German: "${originalRecord.Target_deDE}" → "${record.Target_deDE}"`);
    }
    if (this.tableName === 'webadmin' && originalRecord.maxCharacterLength !== record.maxCharacterLength) {
      changes.push(`Max length: "${originalRecord.maxCharacterLength}" → "${record.maxCharacterLength}"`);
    }

    if (changes.length === 0) {
      delete this.clonedRecords[rowKey];
      this.messageService.add({
        severity: 'info',
        summary: 'No Changes',
        detail: 'No modifications were made to this record'
      });
      if (rowElement) {
        this.locTable?.saveRowEdit(record, rowElement);
      }
      return;
    }

    const recordLabel = this.isSpecialTable ? record.recordId : `${record.recordId} - ${record.field}`;
    const changesMessage = changes.join('<br/>');

    this.confirmationService.confirm({
      message: `
        <strong>Are you sure you want to save these changes?</strong><br/><br/>
        <div style="text-align: left; padding-left: 20px;">
          <strong>Record:</strong> ${recordLabel}<br/><br/>
          <strong>Changes:</strong><br/>
          ${changesMessage}
        </div>
      `,
      header: 'Confirm Save Changes',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          const updateData: any = {};
          if (originalRecord.Source_en !== record.Source_en) updateData.Source_en = record.Source_en;
          if (originalRecord.Target_frFR !== record.Target_frFR) updateData.Target_frFR = record.Target_frFR;
          if (originalRecord.Target_esMx !== record.Target_esMx) updateData.Target_esMx = record.Target_esMx;
          if (originalRecord.Target_deDE !== record.Target_deDE) updateData.Target_deDE = record.Target_deDE;
          if (this.tableName === 'webadmin' && originalRecord.maxCharacterLength !== record.maxCharacterLength) {
            updateData.maxCharacterLength = record.maxCharacterLength;
          }

          const whereClause = this.isSpecialTable ? 'recordId = ?' : 'recordId = ? AND field = ?';
          const params = this.isSpecialTable ? [record.recordId] : [record.recordId, record.field];

          await firstValueFrom(
            this.http.patch(`/api/localization/db/table/${this.tableName}`, {
              data: updateData,
              whereClause,
              params
            })
          );

          delete this.clonedRecords[rowKey];

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Record updated successfully'
          });
          if (rowElement) {
            this.locTable?.saveRowEdit(record, rowElement);
          }
        } catch (error) {
          this.logger.error('Error updating record:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update record'
          });
        }
      },
      reject: () => {
        const index = this.records.findIndex(
          r => this.getRowKey(r) === this.getRowKey(record)
        );
        if (index !== -1) {
          this.records[index] = { ...originalRecord };
          this.locTable?.cancelRowEdit(this.records[index]);
        }
        delete this.clonedRecords[rowKey];
      }
    });
  }

  onRowEditCancel(record: any, index: number) {
    const rowKey = this.getRowKey(record);
    this.records[index] = this.clonedRecords[rowKey];
    delete this.clonedRecords[rowKey];
  }

  deleteRecord(record: any) {
    const recordLabel = this.isSpecialTable ? record.recordId : `${record.recordId} - ${record.field}`;
          const whereClause = this.isSpecialTable ? 'recordId = ?' : 'recordId = ? AND field = ?';
          const params = this.isSpecialTable ? [record.recordId] : [record.recordId, record.field];

    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${recordLabel}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await firstValueFrom(
            this.http.delete(`/api/localization/db/table/${this.tableName}`, {
              body: { whereClause, params }
            })
          );

          // Refresh paginated data after successful delete
          await this.loadRecords(this.currentPage);

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Record deleted successfully'
          });
        } catch (error) {
          this.logger.error('Error deleting record:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete record'
          });
        }
      }
    });
  }

  onActionsDialogShow() {
    // Auto-populate entity name from table name when dialog opens
    if (this.tableName && (!this.bulkLocalizeEntity || this.bulkLocalizeEntity === '')) {
      this.bulkLocalizeEntity = this.tableName;
    }
  }

  refreshData() {
    this.loadRecords(this.currentPage);
    this.actionsDialogVisible = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Refreshed',
      detail: 'Table data has been refreshed'
    });
  }

  clearFilters() {
    this.filters = {};
    this.loadRecords(1);
    this.actionsDialogVisible = false;
    this.messageService.add({
      severity: 'success',
      summary: 'Filters Cleared',
      detail: 'All filters have been cleared'
    });
  }

  exportToCSV() {
    // TODO: Implement CSV export functionality
    this.messageService.add({
      severity: 'info',
      summary: 'Export',
      detail: 'CSV export functionality coming soon'
    });
    this.actionsDialogVisible = false;
  }

  async translateRecord(record: any) {
    if (this.isSpecialTable) return;
    const rowKey = this.getRowKey(record);
    this.translatingRecords[rowKey] = true;
    try {
      await firstValueFrom(
        this.http.post('/api/localization/translate-record', {
          tableName: this.tableName,
          recordId: record.recordId,
          field: record.field
        })
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Translation queued',
        detail: 'Record has been queued for translation'
      });
      this.loadRecords(this.currentPage);
    } catch (error) {
      this.logger.error('Error triggering translation:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to trigger translation'
      });
    } finally {
      delete this.translatingRecords[rowKey];
    }
  }

  async migrateTableFromGridly() {
    if (!this.migrateViewId?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Please enter the Gridly view ID'
      });
      return;
    }
    this.migrateInProgress = true;
    this.migrateResult = null;
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; totalProcessed?: number; batchesProcessed?: number; errors?: string[] }>(
          '/api/worker/special/table/migrate-table-from-gridly',
          { viewId: this.migrateViewId.trim(), batchSize: this.migrateBatchSize || 100, tableName: this.tableName }
        )
      );
      this.migrateResult = res;
      if (res.success) {
        this.messageService.add({
          severity: 'success',
          summary: 'Migration complete',
          detail: `Migrated ${res.totalProcessed ?? 0} records in ${res.batchesProcessed ?? 0} batch(es).`
        });
        this.loadRecords(1);
      } else {
        const errMsg = (res.errors && res.errors.length) ? res.errors.join('; ') : 'Migration had errors';
        this.messageService.add({
          severity: 'warn',
          summary: 'Migration finished with errors',
          detail: errMsg
        });
      }
    } catch (error: any) {
      const msg = error.error?.message || error.message || 'Migration failed';
      this.migrateResult = { success: false, errors: [msg] };
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: msg
      });
    } finally {
      this.migrateInProgress = false;
    }
  }

  /**
   * Parse ID ranges from string input
   * Supports formats like: "1-100", "200-250", "300", or "1-100, 200-250, 300"
   */
  private parseIdRanges(rangeString: string): number[] {
    const ids: number[] = [];
    const ranges = rangeString.split(',').map(r => r.trim());
    
    for (const range of ranges) {
      if (range.includes('-')) {
        // Handle range like "1-100"
        const [start, end] = range.split('-').map(s => parseInt(s.trim(), 10));
        if (!isNaN(start) && !isNaN(end) && start <= end) {
          for (let i = start; i <= end; i++) {
            ids.push(i);
          }
        }
      } else {
        // Handle single ID like "300"
        const id = parseInt(range.trim(), 10);
        if (!isNaN(id)) {
          ids.push(id);
        }
      }
    }
    
    // Remove duplicates and sort
    return [...new Set(ids)].sort((a, b) => a - b);
  }

  async bulkLocalizeByIdRanges() {
    if (!this.bulkLocalizeIdRanges || !this.bulkLocalizeEntity) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please provide both ID ranges and entity name'
      });
      return;
    }

    this.bulkLocalizeInProgress = true;
    this.bulkLocalizeProgress = 0;
    this.bulkLocalizeResult = null;

    try {
      // Parse ID ranges
      const ids = this.parseIdRanges(this.bulkLocalizeIdRanges);
      
      if (ids.length === 0) {
        throw new Error('No valid IDs found in the provided ranges');
      }

      this.messageService.add({
        severity: 'info',
        summary: 'Processing',
        detail: `Processing ${ids.length} IDs for localization...`
      });

      // Process in batches to avoid overwhelming the server
      const batchSize = 100;
      const batches: number[][] = [];
      
      for (let i = 0; i < ids.length; i += batchSize) {
        batches.push(ids.slice(i, i + batchSize));
      }

      let successCount = 0;
      let errorCount = 0;

      // Process each batch
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        
        try {
          const body = {
            ids: batch,
            collection: this.bulkLocalizeEntity
          };

          const response = await firstValueFrom(
            this.http.post<any>('/api/localization/worker/ai-broker/translate', body)
          );

          if (response.success) {
            successCount += batch.length;
          } else {
            errorCount += batch.length;
          }
        } catch (error) {
          console.error(`Error processing batch ${i + 1}:`, error);
          errorCount += batch.length;
        }

        // Update progress
        this.bulkLocalizeProgress = Math.floor(((i + 1) / batches.length) * 100);
      }

      // Show results
      if (errorCount === 0) {
        this.bulkLocalizeResult = {
          success: true,
          message: `Successfully queued ${successCount} records for localization`
        };
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Successfully queued ${successCount} records for localization`
        });
      } else {
        this.bulkLocalizeResult = {
          success: false,
          message: `Processed ${successCount} successfully, ${errorCount} failed`
        };
        this.messageService.add({
          severity: 'warn',
          summary: 'Partial Success',
          detail: `Processed ${successCount} successfully, ${errorCount} failed`
        });
      }

      // Refresh the table after successful localization
      if (successCount > 0) {
        setTimeout(() => {
          this.loadRecords(this.currentPage);
        }, 1000);
      }

    } catch (error: any) {
      console.error('Error in bulk localization:', error);
      const errorMessage = error.error?.message || error.message || 'Failed to process bulk localization';
      this.bulkLocalizeResult = {
        success: false,
        message: errorMessage
      };
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage
      });
    } finally {
      this.bulkLocalizeInProgress = false;
      this.bulkLocalizeProgress = 0;
    }
  }

  getTableTranslateTargetFields(): string[] {
    const fields: string[] = [];
    if (this.tableTranslateTargetFr) fields.push('Target_frFR');
    if (this.tableTranslateTargetEs) fields.push('Target_esMx');
    if (this.tableTranslateTargetDe) fields.push('Target_deDE');
    return fields;
  }

  async bulkTranslateSelected() {
    if (!this.selectedRecords || this.selectedRecords.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No selection',
        detail: 'Select one or more rows to translate.'
      });
      return;
    }

    if (this.tableName === 'webadmin') {
      const recordIds = this.selectedRecords.map((r) => String(r.recordId)).filter((id, i, a) => a.indexOf(id) === i);
      if (recordIds.length === 0) {
        this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'No valid record IDs in selection.' });
        return;
      }
      const targetFields = this.getTableTranslateTargetFields();
      if (targetFields.length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validation',
          detail: 'Select at least one target language in Actions → Bulk translate.'
        });
        return;
      }
      this.tableTranslateInProgress = true;
      this.tableTranslateResult = null;
      try {
        const res = await firstValueFrom(
          this.http.post<{ success: boolean; queued?: number; error?: string }>(
            '/api/localization/translate-special-table',
            { recordIds, tableName: this.tableName, targetFields }
          )
        );
        this.tableTranslateResult = res;
        if (res.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Translation queued',
            detail: (res.queued === 0 ? 'No records with Source_en found to translate.' : `Queued ${res.queued} record(s) for AI translation.`)
          });
          if ((res.queued ?? 0) > 0) {
            this.selectedRecords = [];
            setTimeout(() => this.loadRecords(this.currentPage), 2000);
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: res.error ?? 'Translation request failed.'
          });
        }
      } catch (error: any) {
        const msg = error.error?.message || error.message || 'Request failed';
        this.tableTranslateResult = { success: false, error: msg };
        this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
      } finally {
        this.tableTranslateInProgress = false;
      }
      return;
    }

    // Non-webadmin: build ID list from selected (unique recordIds) and run bulk localize
    const uniqueIds = [...new Set(this.selectedRecords.map((r) => Number(r.recordId)).filter((n) => !isNaN(n)))];
    if (uniqueIds.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'No valid record IDs in selection.'
      });
      return;
    }
    this.bulkLocalizeIdRanges = uniqueIds.sort((a, b) => a - b).join(',');
    if (!this.bulkLocalizeEntity) {
      this.bulkLocalizeEntity = this.tableName;
    }
    await this.bulkLocalizeByIdRanges();
    this.selectedRecords = [];
  }

}

